/**
 * Phase 5 visual primitives — the small components that compose into
 * KidCard, PerspectiveCard, and DashboardPage.
 *
 * All components in this file consume the warm summer-light palette
 * from `styles/tokens.css` (STYLE_GUIDE.md §2.1). None of them use
 * raw hex values — every color comes from a `var(--*)` token.
 *
 * Numbers always render in `font-mono tabular-nums` per STYLE_GUIDE.md
 * §2.3. The `ConfidenceTilde` component renders a subtle `~` prefix
 * that signals an estimated value.
 *
 * `EighteenSummersDotGrid` is the app's signature visual — 18 dots
 * in a 3x6 grid where each dot is either `used`, `now`, or `remaining`.
 * It is NOT to be redesigned (CLAUDE.md design rule 4).
 */

import type { ReactNode } from "react";

// ---------------------------------------------------------------------------
// ConfidenceTilde — the subtle "~" prefix for estimated values
// ---------------------------------------------------------------------------

interface ConfidenceTildeProps {
  confidence: "exact" | "estimated";
}

export function ConfidenceTilde({ confidence }: ConfidenceTildeProps) {
  if (confidence === "exact") return null;
  return (
    <span
      className="inline-block mr-0.5 text-ink-muted/75"
      aria-label="estimated value"
      title="This is estimated because the birth day isn't known"
    >
      ~
    </span>
  );
}

// ---------------------------------------------------------------------------
// NumberHero — the big tabular-nums number with an optional tilde
// ---------------------------------------------------------------------------

interface NumberHeroProps {
  value: number;
  confidence: "exact" | "estimated";
  size?: "card" | "hero" | "drill";
  className?: string;
}

export function NumberHero({
  value,
  confidence,
  size = "card",
  className = "",
}: NumberHeroProps) {
  const sizeClass =
    size === "hero"
      ? "text-[48px] md:text-[68px] leading-none"
      : size === "drill"
        ? "text-[56px] md:text-[96px] leading-none"
        : "text-[28px] md:text-[36px] leading-none";

  return (
    <div
      className={`font-mono tabular-nums font-semibold text-ink ${sizeClass} ${className}`}
    >
      <ConfidenceTilde confidence={confidence} />
      {formatInteger(value)}
    </div>
  );
}

function formatInteger(value: number): string {
  // Large integers get thousands separators; decimals (percent
  // values) render with one decimal place.
  if (Number.isInteger(value)) {
    return value.toLocaleString("en-US");
  }
  return value.toFixed(1);
}

// ---------------------------------------------------------------------------
// EighteenSummersDotGrid — the signature visual
// ---------------------------------------------------------------------------

interface DotGridProps {
  used: number;
  total?: number;
  /** Optional label for accessibility. */
  label?: string;
}

/**
 * 18 dots in a 3x6 grid. States:
 *   - `used`: dark amber (past summers)
 *   - `now`: coral, pulsing gently (the current summer position)
 *   - `remaining`: pale amber (future summers)
 *
 * The `now` dot is inserted at position `used` (i.e., right after
 * the last used dot). On the current-year summer, the now dot
 * represents "this summer is in progress."
 */
export function EighteenSummersDotGrid({
  used,
  total = 18,
  label,
}: DotGridProps) {
  // Build 18 dot state entries. Clamp `used` to [0, total].
  const clampedUsed = Math.max(0, Math.min(used, total));
  const dots: Array<"used" | "now" | "remaining"> = [];
  for (let i = 0; i < total; i += 1) {
    if (i < clampedUsed) {
      dots.push("used");
    } else if (i === clampedUsed) {
      dots.push("now");
    } else {
      dots.push("remaining");
    }
  }

  const ariaLabel = label ?? `${total - clampedUsed} of ${total} summers remaining`;

  return (
    <div
      className="inline-grid grid-cols-6 gap-[8px] md:gap-[10px]"
      role="img"
      aria-label={ariaLabel}
    >
      {dots.map((state, i) => (
        <span
          key={i}
          className={[
            "w-[12px] h-[12px] md:w-[14px] md:h-[14px] rounded-full",
            state === "used" && "bg-[var(--amber-deep)]",
            state === "now" && "bg-[var(--coral)] animate-nowPulse",
            state === "remaining" && "bg-[var(--amber-soft)] border border-[var(--amber)]/30",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatBar — one of the three stat bars on a KidCard
// ---------------------------------------------------------------------------

type StatBarTone = "coral" | "honey" | "amber";

interface StatBarProps {
  value: number;
  label: string;
  confidence: "exact" | "estimated";
  /** Progress meter fill percentage, 0-100. Hidden when 0. */
  percentRemaining?: number | undefined;
  tone?: StatBarTone | undefined;
}

export function StatBar({
  value,
  label,
  confidence,
  percentRemaining,
  tone = "coral",
}: StatBarProps) {
  const toneClasses: Record<StatBarTone, { bg: string; border: string; fill: string }> = {
    coral: {
      bg: "bg-gradient-to-br from-[var(--coral-bar-from)] to-[var(--coral-bar-to)]",
      border: "border-[var(--coral-deep)]/20",
      fill: "bg-[var(--coral-deep)]",
    },
    honey: {
      bg: "bg-gradient-to-br from-[var(--honey-bar-from)] to-[var(--honey-bar-to)]",
      border: "border-[var(--honey-deep)]/20",
      fill: "bg-[var(--honey-deep)]",
    },
    amber: {
      bg: "bg-gradient-to-br from-[var(--amber-bar-from)] to-[var(--amber-bar-to)]",
      border: "border-[var(--amber-deep)]/20",
      fill: "bg-[var(--amber-deep)]",
    },
  };
  const t = toneClasses[tone];

  return (
    <div
      className={`flex items-center gap-3 md:gap-4 rounded-md border px-3 md:px-4 py-3 ${t.bg} ${t.border}`}
    >
      <div className="font-mono tabular-nums text-ink font-semibold text-[22px] md:text-[28px] leading-none min-w-[56px] md:min-w-[72px] text-right">
        <ConfidenceTilde confidence={confidence} />
        {formatInteger(value)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-sans text-[13px] text-ink-soft leading-snug">
          {label}
        </div>
        {typeof percentRemaining === "number" && percentRemaining > 0 && (
          <div className="mt-2 h-[4px] rounded-full bg-ink/10 overflow-hidden">
            <div
              className={`h-full rounded-full ${t.fill}`}
              style={{ width: `${Math.max(0, Math.min(100, percentRemaining))}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PinButton — the one place RWAR Red is allowed (CLAUDE.md §4 rule 3)
// ---------------------------------------------------------------------------

interface PinButtonProps {
  pinned: boolean;
  onToggle: () => void;
  disabled?: boolean;
  label?: string;
}

export function PinButton({
  pinned,
  onToggle,
  disabled = false,
  label,
}: PinButtonProps) {
  const aria = label ?? (pinned ? "Unpin this perspective" : "Pin this perspective");
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={pinned}
      aria-label={aria}
      className={[
        "inline-flex items-center justify-center",
        "w-10 h-10 rounded-full",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-amber",
        "disabled:opacity-40 disabled:cursor-not-allowed",
        pinned ? "text-[var(--rwar-red)]" : "text-ink-muted hover:text-ink-soft",
      ].join(" ")}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill={pinned ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <path d="M14 2l-1 7 4 3v3h-5l-1 7h-2l-1-7H3v-3l4-3-1-7z" />
      </svg>
    </button>
  );
}

// ---------------------------------------------------------------------------
// TierBadge — tier label for PerspectiveCards on the child-detail page
// ---------------------------------------------------------------------------

interface TierBadgeProps {
  tier: 1 | 2 | 3 | 4 | 5;
}

const TIER_NAMES: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: "Big Window",
  2: "Fleeting Magic",
  3: "Peer Shift",
  4: "Everyday",
  5: "Big Picture",
};

export function TierBadge({ tier }: TierBadgeProps) {
  return (
    <span className="inline-block font-sans text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-muted">
      {TIER_NAMES[tier]} · Tier {tier}
    </span>
  );
}

export { TIER_NAMES };

// ---------------------------------------------------------------------------
// SectionHeading — tier section headers on ChildPerspectivesPage
// ---------------------------------------------------------------------------

interface SectionHeadingProps {
  eyebrow: string;
  title: ReactNode;
  description?: string;
}

export function SectionHeading({
  eyebrow,
  title,
  description,
}: SectionHeadingProps) {
  return (
    <div className="mb-5">
      <div className="font-sans text-[11px] uppercase tracking-[0.14em] font-semibold text-coral-deep mb-1">
        {eyebrow}
      </div>
      <h2 className="font-serif text-[28px] text-ink">{title}</h2>
      {description !== undefined && (
        <p className="mt-1 font-sans text-[13px] text-ink-muted">{description}</p>
      )}
    </div>
  );
}
